import { onSnapshot, type Unsubscribe } from "firebase/firestore";
import { createGlobalState } from "@vueuse/core";
import { useFirestore } from "~/composables/useFirestore";

// Two-level feature flag controlling whether the IDB data cache is used for
// Layer A bootstrap data. Default OFF (= use cache). When ON, idbCache reads
// return null so versionSync / baseRepo always refetch from cloud.
//
// Effective resolution (per-store overrides global):
//   bypassCache = perStore.featureFlags?.bypassDataCache
//              ?? global.bypassDataCache
//              ?? false
//
// The Firestore listener writes the resolved value into localStorage. Boot-
// time code paths (idbCache.readCacheEntry, versionSync.reconcileOne) read it
// SYNCHRONOUSLY from localStorage via `readBypassCacheFromLocalStorage()` —
// they can't await Firestore. A change in Firestore therefore takes effect on
// the NEXT page reload, by design.

const LS_KEY = "pwaDataCacheFlag";

type CacheFlagPayload = {
  bypassCache: boolean;
  source: "global" | "store" | "default";
  updatedAt: number;
};

// In-process cache so we don't JSON.parse on every IDB read. Locked at first
// access for the lifetime of the page; updates only take effect on reload
// (matching the documented contract).
let _cachedBypass: boolean | null = null;

export function readBypassCacheFromLocalStorage(): boolean {
  if (_cachedBypass !== null) return _cachedBypass;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) {
      _cachedBypass = false;
      return false;
    }
    const parsed = JSON.parse(raw) as Partial<CacheFlagPayload>;
    _cachedBypass = parsed?.bypassCache === true;
  } catch {
    _cachedBypass = false;
  }
  return _cachedBypass;
}

function writeFlagToLocalStorage(payload: CacheFlagPayload) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn("[pwaCacheFlag] localStorage write failed", err);
  }
}

export const usePwaCacheFlag = createGlobalState(() => {
  // `effectiveBypass` reflects the latest computed value from Firestore. It is
  // exposed for UI/debug; the boot-time path reads localStorage directly so
  // it works before this composable runs.
  const effectiveBypass = ref<boolean>(readBypassCacheFromLocalStorage());
  const source = ref<"global" | "store" | "default">("default");
  const isWatching = ref(false);

  let unsubGlobal: Unsubscribe | null = null;
  let unsubStore: Unsubscribe | null = null;

  let globalValue: boolean | undefined = undefined;
  let storeValue: boolean | undefined = undefined;

  const tristate = (v: unknown): boolean | undefined =>
    v === true ? true : v === false ? false : undefined;

  function recompute() {
    let nextValue: boolean;
    let nextSource: "global" | "store" | "default";
    if (storeValue !== undefined) {
      nextValue = storeValue;
      nextSource = "store";
    } else if (globalValue !== undefined) {
      nextValue = globalValue;
      nextSource = "global";
    } else {
      nextValue = false;
      nextSource = "default";
    }

    const prev = effectiveBypass.value;
    effectiveBypass.value = nextValue;
    source.value = nextSource;

    writeFlagToLocalStorage({
      bypassCache: nextValue,
      source: nextSource,
      updatedAt: Date.now(),
    });

    if (prev !== nextValue) {
      console.log(
        `[pwaCacheFlag] bypass=${nextValue} (source=${nextSource}) — takes effect on next reload`
      );
    }
  }

  function startWatching() {
    if (isWatching.value) return;
    const firestore = useFirestore();

    unsubGlobal = onSnapshot(
      firestore.getFeatureFlagsRef(),
      (snap) => {
        if (snap.metadata.fromCache) return;
        const data = snap.data();
        globalValue = tristate(data?.bypassDataCache);
        recompute();
      },
      (err) => console.warn("[pwaCacheFlag] global snapshot error", err)
    );

    unsubStore = onSnapshot(
      firestore.getRootDataRef(),
      (snap) => {
        if (snap.metadata.fromCache) return;
        const data = snap.data();
        storeValue = tristate(data?.featureFlags?.bypassDataCache);
        recompute();
      },
      (err) => console.warn("[pwaCacheFlag] store snapshot error", err)
    );

    isWatching.value = true;
  }

  function stopWatching() {
    if (unsubGlobal) {
      unsubGlobal();
      unsubGlobal = null;
    }
    if (unsubStore) {
      unsubStore();
      unsubStore = null;
    }
    isWatching.value = false;
  }

  return { effectiveBypass, source, isWatching, startWatching, stopWatching };
});
